/**
 * <ComingSoonPage> — shared placeholder for sysadmin IA cells that
 * are scaffolded but not yet built.
 *
 * Per docs/SYSADMIN-PLATFORM-SPEC.md §6 the IA shows up immediately
 * even when individual modules aren't implemented. This component
 * gives every stub route a consistent landing surface that:
 *   - shows the spec section + scheduled wave
 *   - lets the operator see where the feature will live
 *   - links back to the spec doc so they can read the contract
 *
 * Use:
 *   <ComingSoonPage
 *     icon={Network}
 *     title="MCP server registry"
 *     specRef="§3.3 + §10.2 #3b"
 *     wave="Wave 4"
 *     description="Tier-2 vetted catalog + Forge-promotion queue + sandbox monitor."
 *     bullets={[...]}
 *   />
 */

import type { LucideIcon } from 'lucide-react';
import { Construction, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export interface ComingSoonPageProps {
  icon: LucideIcon;
  title: string;
  /** Spec section reference, e.g. "§3.3" or "§4.4 ⭐". */
  specRef?: string;
  /** Scheduled wave label, e.g. "Wave 4". */
  wave?: string;
  description: string;
  /** Optional bullet list of capabilities the cell will deliver. */
  bullets?: string[];
}

export function ComingSoonPage({
  icon: Icon,
  title,
  specRef,
  wave = 'Wave 4',
  description,
  bullets,
}: ComingSoonPageProps) {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Icon className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <div className="flex items-center gap-2 mt-1">
            {specRef && (
              <Badge variant="outline" className="text-xs gap-1 font-mono">
                <FileText className="h-3 w-3" />
                {specRef}
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs">{wave}</Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Construction className="h-4 w-4 text-muted-foreground" />
            Scaffold only — module ships in {wave}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        {bullets && bullets.length > 0 && (
          <CardContent>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-foreground/40 mt-0.5">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        )}
      </Card>

      <div className="text-xs text-muted-foreground">
        See <code>docs/SYSADMIN-PLATFORM-SPEC.md</code> for the full
        IA, asset inheritance model, and build phasing.
      </div>
    </div>
  );
}
